/**
 * Pending Returns — the returnable custody register.
 *
 * This is the screen the whole returnable half of the feature exists for: what is
 * outside, who has it, and how long it has been gone. Nothing anywhere in gate
 * pass moves stock —
 * a returnable pass never posted in the first place.
 *
 * Reads the hub's already-loaded /outstanding queue; it fires no request of its own.
 */
import React, { useState, useMemo } from 'react';
import { Box, IconButton, LinearProgress, Tooltip, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import InvDataGrid from '../InvDataGrid';
import ConfirmDialog from '../ConfirmDialog';
import SectionCard from '../SectionCard';
import InvButton from '../InvButton';
import { closeNotReturned } from '../../api/gatePass';
import { GATE_PASS_PURPOSE_LABELS } from '../../constants/gatePass';
import { formatDate } from '../../utils';
import useInvRole from '../../hooks/useInvRole';
import {
  getGatePassActions,
  returnProgress,
  isOverdue,
  daysOut,
  daysOverdue,
  GATE_PASS_ACTIONS as A,
} from '../../utils/gatePassEntity';
import { IPD_COLORS } from '../../theme';

export default function GatePassOutstandingPanel({ base, queues, overdueOnly, onOverdueOnlyChange }) {
  const history = useHistory();
  const { can } = useInvRole();

  const [writeOffTarget, setWriteOffTarget] = useState(null);
  const [reason, setReason] = useState('');
  const [acting, setActing] = useState(false);

  const all = queues?.outstanding || [];
  const rows = overdueOnly ? all.filter(gp => isOverdue(gp)) : all;

  async function runWriteOff() {
    if (!writeOffTarget) return;
    setActing(true);
    try {
      // API: POST /inventory/gate-pass/{id}/close-not-returned/
      await closeNotReturned(writeOffTarget.id, { reason: reason.trim() });
      toast.success('Closed as not returned');
      setWriteOffTarget(null);
      setReason('');
      queues?.reload?.();
    } catch (e) {
      toast.error(e.message || 'Action failed');
    } finally {
      setActing(false);
    }
  }

  const columns = useMemo(() => [
    { field: 'gate_pass_number', headerName: 'Gate Pass #', width: 180 },
    {
      field: 'purpose',
      headerName: 'Purpose',
      width: 130,
      valueFormatter: p => GATE_PASS_PURPOSE_LABELS[p.value] || p.value || '—',
    },
    { field: 'party_name', headerName: 'Who has it', width: 200 },
    {
      field: 'gate_out_at',
      headerName: 'Days out',
      width: 100,
      valueGetter: p => daysOut(p.row),
      renderCell: p => <span>{p.value == null ? '—' : `${p.value}d`}</span>,
    },
    {
      field: 'expected_return_date',
      headerName: 'Expected back',
      width: 160,
      renderCell: (p) => {
        const late = isOverdue(p.row);
        return (
          <Typography sx={{ fontSize: 13, color: late ? IPD_COLORS.error.text : IPD_COLORS.textPrimary, fontWeight: late ? 600 : 400 }}>
            {formatDate(p.value)}
            {late ? ` · ${daysOverdue(p.row)}d late` : ''}
          </Typography>
        );
      },
    },
    {
      field: 'lines',
      headerName: 'Returned',
      width: 170,
      sortable: false,
      renderCell: (p) => {
        const { returned, total, pct } = returnProgress(p.row.lines);
        return (
          <Box sx={{ width: '100%' }}>
            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
              {returned} / {total}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                height: 4,
                borderRadius: 2,
                backgroundColor: 'rgba(13,35,63,0.08)',
                '& .MuiLinearProgress-bar': { backgroundColor: IPD_COLORS.success.text },
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 130,
      sortable: false,
      renderCell: (p) => {
        const actions = getGatePassActions(p.row, can);
        return (
          <Box sx={{ display: 'flex', gap: 0.25 }}>
            <Tooltip title="View">
              <IconButton
                size="small"
                onClick={() => history.push(`${base}/${p.row.id}`)}
                sx={{ color: IPD_COLORS.primary }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {actions.has(A.RECORD_RETURN) && (
              <Tooltip title="Record return">
                <IconButton
                  size="small"
                  onClick={() => history.push(`${base}/${p.row.id}/return`)}
                  sx={{ color: IPD_COLORS.success.text }}
                >
                  <KeyboardReturnIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {actions.has(A.CLOSE_NOT_RETURNED) && (
              <Tooltip title="Close as not returned">
                <IconButton
                  size="small"
                  onClick={() => { setWriteOffTarget(p.row); setReason(''); }}
                  sx={{ color: IPD_COLORS.error.text }}
                >
                  <ReportProblemOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ], [base, history, can]);

  return (
    <Box>
      <SectionCard
        title="Pending Returns"
        subtitle="Returnable items that are outside and not back yet"
        actions={(
          <InvButton
            size="small"
            variant={overdueOnly ? 'contained' : 'outlined'}
            onClick={() => onOverdueOnlyChange(!overdueOnly)}
          >
            {overdueOnly ? 'Showing overdue only' : 'Show overdue only'}
          </InvButton>
        )}
      >
        <Typography variant="caption" sx={{ color: IPD_COLORS.textSecondary, display: 'block', mb: 1 }}>
          {rows.length} item{rows.length === 1 ? '' : 's'} outside
        </Typography>

        <InvDataGrid
          rows={rows}
          columns={columns}
          loading={queues?.loading}
          error={queues?.error}
          onRetry={queues?.reload}
          getRowId={r => r.id}
          emptyMessage={overdueOnly ? 'Nothing is overdue.' : 'Nothing is outside right now.'}
        />
      </SectionCard>

      <ConfirmDialog
        open={Boolean(writeOffTarget)}
        title="Close as Not Returned"
        description={`"${writeOffTarget?.gate_pass_number}" will be closed as not returned and stop appearing as overdue. Nothing is written off your stock — correct the books with a stock adjustment if they are wrong.`}
        destructive
        requireReason
        reasonLabel="Why is it not coming back?"
        reasonValue={reason}
        onReasonChange={setReason}
        loading={acting}
        onConfirm={runWriteOff}
        onCancel={() => setWriteOffTarget(null)}
        confirmLabel="Close as not returned"
      />
    </Box>
  );
}
